/**
 * Approvals queue table — 10 columns per LOVABLE_APPROVALS_PAGE.md.
 * Inline Approve/Deny/Info actions; orphan-aware (disabled when
 * `agent.found === false`).
 */
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { OwnerPill } from "@/components/owner-pill";
import { CheckCircle2, XCircle, HelpCircle, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ApprovalRequestDTO } from "@/types/lighthouseApprovals";
import {
  APPROVAL_STATE_LABEL,
  type ApprovalState,
} from "@/types/lighthouseApprovals";
import {
  ageIsStale,
  classificationClass,
  riskBarColor,
  shortAge,
  stateChipClass,
} from "./approvalsUi";

interface Props {
  requests: ApprovalRequestDTO[];
  onRowClick: (req: ApprovalRequestDTO) => void;
  onApprove: (req: ApprovalRequestDTO) => void;
  onDeny: (req: ApprovalRequestDTO) => void;
  onInfo: (req: ApprovalRequestDTO) => void;
}

export function ApprovalsTable({ requests, onRowClick, onApprove, onDeny, onInfo }: Props) {
  if (requests.length === 0) return null;
  return (
    <TooltipProvider>
      <div className="rounded-md border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[20%]">Agent</TableHead>
              <TableHead className="w-[14%]">Device</TableHead>
              <TableHead className="w-[12%]">Requested by</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead className="w-[22%]">Reason</TableHead>
              <TableHead className="w-[8%]">Risk</TableHead>
              <TableHead className="w-[8%]">Class</TableHead>
              <TableHead className="w-[6%]">Aged</TableHead>
              <TableHead className="w-[8%]">Status</TableHead>
              <TableHead className="w-[10%] text-right">Decide</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {requests.map((r) => {
              const orphan = r.agent?.found === false;
              const score = r.agent?.riskScore ?? 0;
              const name = r.agent?.displayName ?? r.agentId;
              const publisher = r.agent?.canonicalPublisher ?? r.publisher ?? "—";
              const hostname = r.agent?.hostname ?? r.deviceId ?? "—";
              const requester = r.requester === "system" ? "Smplify (auto)" : r.requester ?? "—";
              const cls = r.agent?.classification ?? "unknown";
              return (
                <TableRow
                  key={r.id}
                  className="cursor-pointer"
                  onClick={() => onRowClick(r)}
                >
                  <TableCell>
                    <div className="font-medium text-foreground truncate">{name}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{publisher}</div>
                    {orphan && (
                      <Badge variant="outline" className="mt-1 bg-warning-muted text-warning border-warning/30 gap-1">
                        <AlertTriangle className="h-3 w-3" /> Agent no longer on fleet
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-foreground">{hostname}</TableCell>
                  <TableCell className="text-xs">
                    {r.requester === "system" ? (
                      <span className="text-muted-foreground">{requester}</span>
                    ) : (
                      <OwnerPill consoleUser={r.requester} size="sm" inert />
                    )}
                  </TableCell>
                  <TableCell>
                    <OwnerPill consoleUser={r.agent?.consoleUser} size="sm" inert />
                  </TableCell>
                  <TableCell className="text-xs">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="line-clamp-2 text-foreground">
                          {r.requestReason ?? "—"}
                        </span>
                      </TooltipTrigger>
                      {r.requestReason && (
                        <TooltipContent side="top" className="max-w-md">
                          {r.requestReason}
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="tabular-nums text-sm w-7">{score}</span>
                      <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                        <div className={cn("h-full", riskBarColor(score))} style={{ width: `${score}%` }} />
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={cn("capitalize", classificationClass(cls))}>
                      {cls}
                    </Badge>
                  </TableCell>
                  <TableCell className={cn("text-xs tabular-nums", ageIsStale(r.createdAt) && "text-warning")}>
                    {shortAge(r.createdAt)}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={stateChipClass(r.state)}>
                      {APPROVAL_STATE_LABEL[r.state] ?? r.state}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="inline-flex gap-1">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            disabled={orphan || r.state !== "pending" && r.state !== "info_requested"}
                            onClick={() => onApprove(r)}
                          >
                            <CheckCircle2 className="h-4 w-4 text-success" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Approve</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            disabled={orphan || r.state !== "pending" && r.state !== "info_requested"}
                            onClick={() => onDeny(r)}
                          >
                            <XCircle className="h-4 w-4 text-destructive" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Deny</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            disabled={orphan || r.state !== "pending"}
                            onClick={() => onInfo(r)}
                          >
                            <HelpCircle className="h-4 w-4 text-info" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Ask info</TooltipContent>
                      </Tooltip>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </TooltipProvider>
  );
}
